import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { defer, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';
import { from } from 'rxjs';
import { AppSettings } from '../../app.settings';
import { TabStorageService } from '../tabstorage/tabstorage.service';
import { CREDENTIAL_REQUEST } from '../session/credential-request';
import { ApiService } from '../api/api.service';
import { shouldAttemptApiRefresh, refreshStoredJwtToken, canClearRequestCredentials, repeatApiRequest,
  forceLogout, hasValidStoredJwtToken } from './api-auth-policy';

export interface ApiRequest {
  method: string;
  url: string;
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, any>;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
  withCredentials?: boolean;
}

/** Explicit transport for resource services. It owns auth/replay, so the global
 * HttpClient interceptor must not also process these requests. */
@Injectable({providedIn: 'root'})
export class ApiTransport {
  private readonly http: HttpClient;
  constructor(backend: HttpBackend) { this.http = new HttpClient(backend); }

  createRequest(options: ApiRequest): Observable<HttpResponse<any>> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(options.params || {})) {
      for (const item of Array.isArray(value) ? value : [typeof value === 'object' ? JSON.stringify(value) : value]) {
        params = params.append(key, String(item));
      }
    }
    let headers = new HttpHeaders();
    for (const [key, value] of Object.entries(options.headers || {})) {
      if (value !== undefined) { headers = headers.set(key, value); }
    }
    const request = new HttpRequest(options.method.toUpperCase(), options.url, options.data, {
      headers, params, responseType: options.responseType || 'json', withCredentials: options.withCredentials || false,
    });
    return defer(() => {
      ApiService.spinnerIncrement();
      return this.send(request).pipe(catchError(error => {
        const context = error?.request?.context?.get(CREDENTIAL_REQUEST);
        if (context && !context.current()) { return throwError(new Error('Request session context changed')); }
        if (error.status === 401 && shouldAttemptApiRefresh(error)) {
          return from(refreshStoredJwtToken(context || undefined)).pipe(switchMap(() => {
            if (context && !context.current()) { return throwError(new Error('Request session context changed')); }
            return repeatApiRequest(error) as Observable<HttpResponse<any>>;
          }), catchError(replayError => {
            if (canClearRequestCredentials(context, replayError)) { forceLogout(); }
            return throwError(replayError);
          }));
        }
        if (error.status === 401 || (error.status === 403 && !hasValidStoredJwtToken())) { forceLogout(); }
        return throwError(error);
      }), finalize(() => ApiService.spinnerDecrement()));
    });
  }

  private send(request: HttpRequest<any>): Observable<HttpResponse<any>> {
    let context = request.context.get(CREDENTIAL_REQUEST);
    if (!context && this.sessionRequest(request)) {
      context = TabStorageService.credentials.capture();
      if (context.token) {
        request = request.clone({context: request.context.set(CREDENTIAL_REQUEST, context),
          headers: request.headers.set('Authorization', `Bearer ${context.token}`)});
      } else { context = null; }
    }
    if (context && !context.current()) { return throwError(new Error('Request session context changed')); }
    const sent = request;
    return this.http.request(request).pipe(
      filter((event): event is HttpResponse<any> => event instanceof HttpResponse),
      catchError(error => {
        error.request = sent;
        // Existing service consumers inspect data as well as HttpErrorResponse.error.
        error.data = error.error;
        error.repeatRequest = (replay: HttpRequest<any>) => this.send(replay);
        return throwError(error);
      }),
    );
  }

  private sessionRequest(request: HttpRequest<any>): boolean {
    const url = request.url;
    if (request.headers.has('x-global-configs-auth') || url.includes('/auth') || url.includes('globalconfig.api')
      || url.includes('infra-mgmt.api') || url.includes('be-playground.api') || url.includes(AppSettings.getIntegrationEndpoint())) {
      return false;
    }
    if (!/^https?:\/\//i.test(url)) { return true; }
    try {
      const host = new URL(url).host;
      return host === new URL(AppSettings.getEndpoint()).host || host.endsWith('.api.wallit.app');
    } catch (_) { return url.startsWith(AppSettings.getEndpoint()); }
  }
}
